import { useEffect, useState, useRef } from "react";
import { useAppSelector, useAppDispatch } from "src/store/hooks";
import { useNavigate } from "react-router";
import moment from "moment";
import Swal from "sweetalert2";
import { Invitation } from "sip.js";
import { toast } from "react-toastify";

// Services
import { ws } from "src/services/ws";
import { createDialerInstance, DialerInstance, DialerStatus } from "src/services/dialer";

// Actions
import {
  startCallConference,
  finishCallConference,
  getCallDetails,
  updateCallConference,
  logExternalCall,
  finishExternalCall,
  getUdaDetailsById,
  createCallConference,
  sendCallRequestResponse,
  updateExternalCall,
  getStatusFinalizadorByAderente,
  getExternalCalls,
} from "src/store/call/actions";
import {
  callDataLoaded,
  callEnded,
  addMessage,
  callFeaturesLoaded,
  setTransferStatus,
  addDialerInstance,
  addPstnCall,
  updateCallObject,
  updateTwilioTokens,
  updatePstnCall,
  updateCall,
} from "src/store/call/reducer";
import { setIdle } from "src/store/context/reducer";

// Video+Chat
import VideoChat from "./screens/video-chat";
import TextChat from "./screens/text-chat";
import Call from "src/models/call";
import Feedback from "./screens/feedback";
import ScreenLoader from "src/components/screen-loader";

// Helpers & Serivces
import { fetchRetry, sendChromeEvent } from "src/helpers";
import { Recorder } from "src/services/recorder";

// Models
import PSTNCall, { DialerPSTN } from "src/models/pstn-call";
import { TransferEvent } from "src/types/transfer-object";
import ExtensionChat from "./screens/extension-chat";
import ReversaCall from "./screens/reversa";
import Uda from "src/models/uda";

export default function CallScreen() {
  // Hooks
  const { incomingCallId, udaQueueId, callDetails, tempData, twilioRoomName, twilioToken, transferPaSelected } = useAppSelector((state) => state.call);
  const { accessToken, userInfo } = useAppSelector((state) => state.auth);
  const { sip } = useAppSelector((state) => state.config);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Refs
  const twilioRef = useRef(null);
  const callEnding = useRef<boolean>(false);
  const udaTypingInterval = useRef<number | undefined>(undefined);
  const resolveRef = useRef<Function | null>(null);
  const currentCallRef = useRef(callDetails);
  const transferPaSelectedRef = useRef(transferPaSelected);
  const endTransferenceCb = useRef<Function | null>(null);
  const audioElm = useRef<HTMLAudioElement | null>(null);
  const dialerInstances: Array<DialerInstance> = [];

  // Vars
  const wsBinds: Array<number> = [];

  // Inner state
  const [state, setState] = useState<{
    loading: boolean;
    udaTyping: boolean;
    waitingFeedback: boolean;
    videoCallback?: Function;
  }>({
    loading: true,
    udaTyping: false,
    waitingFeedback: false,
  });

  // => Methods
  // Register start conference
  const registerStartCallConference = async () => {
    if (callDetails) {
      const version = import.meta.env.PACKAGE_VERSION;

      const { success, data } = await startCallConference(callDetails.id, callDetails.aderenteSlug, {
        version,
        ramal: sip.paNumber,
      });

      if (success) {
        dispatch(
          callFeaturesLoaded({
            features: data.features,
            gravar_chamada: data.gravar_chamada,
          }),
        );
      }

      return { success, data };
    }

    return {
      success: false,
      message: "Falha ao iniciar conferencia.",
    };
  };

  // Fluxo ICOM_VOZ
  const initReversaCall = async () => {
    setState((s) => ({ ...s, loading: false }));

    const updateCallObj: any = {};

    let voipDialerInstance = callDetails?.dialerInstances?.find((d) => d.instanceName === "VOIP");

    if (!voipDialerInstance || !callDetails) {
      toast.error("Nenhuma instancia de discador encontrada.");
      return dispatch(setIdle());
    }

    // Get details of UDA
    const udaDetailsResult = await getUdaDetailsById(callDetails.uda.id, callDetails.aderenteSlug);

    // Map services and UDA
    if (udaDetailsResult.success) {
      const uda = new Uda(udaDetailsResult.data);
      updateCallObj.uda = uda;
    }

    callDetails.pstnNumbers[0].active = true;
    updateCallObj.pstnNumbers = [...callDetails.pstnNumbers];

    dispatch(updateCallObject(updateCallObj));

    // VOIP FLUX
    function onVoipReceived(session: Invitation) {
      session.accept();

      const calledNumber = session.remoteIdentity.uri.user;
      if (calledNumber) voipDialerInstance?.updateCurrentNumber(calledNumber);

      // Notify UDA
      ws.sendMessage({ type: "PA_WAITING_UDA_CALLBACK", data: {} });
    }

    // => Dialer binds
    // On some call attended
    voipDialerInstance.on("onCallAnswered", async ({ args: { mediaStream, currentCall, ramal } }: { args: { mediaStream: MediaStream; currentCall: DialerPSTN; ramal: string } }) => {
      const foundActivePstn = currentCallRef.current?.pstnNumbers.find((n) => n.active);

      // Add streams
      Recorder.addMediaStreams([mediaStream]);

      // Register call
      if (foundActivePstn) {
        const { success, data, message } = await logExternalCall(currentCallRef.current?.aderenteSlug!, {
          tipo_ligacao: "atendida",
          numero_externo: currentCall.numberCalled,
          numero_efetivo: currentCall.numberCalled,
          // chamada_interna: null,
          ramal_atendente: sip.paNumber,
          ramal_chamada: ramal,
          inicio_chamada: moment().utcOffset(-3).format("YYYY-MM-DDTHH:mm:ss"),
          status: "atendida",
        });

        if (!success) return console.error(message);

        // Add IDs
        foundActivePstn.externalId = data.id;
        foundActivePstn.called = true;

        return dispatch(updatePstnCall(foundActivePstn));
      }
    });

    // On transfer incoming
    voipDialerInstance.on("onCallReceived", ({ args }: { args: { session: Invitation } }) => onVoipReceived(args.session));

    // On Hangup
    voipDialerInstance.on("onCallHangup", async ({ args: { currentCall } }: { args: { currentCall: DialerPSTN } }) => {
      const foundActivePstn = currentCallRef.current?.pstnNumbers.find((n) => n.active);

      if (foundActivePstn?.externalId) await finishExternalCall(currentCallRef.current?.aderenteSlug!, foundActivePstn.externalId);
    });

    // Save instances
    dialerInstances.push(voipDialerInstance);

    // WS Binds
    wsBinds.push(
      ws.on("PA_WAITING_UDA_CALLBACK_INFO", async (eventData: any) => {
        const serviceId = eventData.serviceId;
        const currentExternalCall = callDetails.pstnNumbers[0];

        const { data: call } = await createCallConference(callDetails.uda.id, sip.paNumber, serviceId, callDetails.aderenteSlug, "reversa");
        const callDetailsResponse = await getCallDetails(call.id);
        const finishStatuses = await getStatusFinalizadorByAderente(callDetailsResponse.data.aderente_slug);

        // Send In App Notification
        await sendCallRequestResponse({
          udaId: callDetails.uda.id,
          serviceId,
          aderenteSlug: currentCallRef.current?.uda.aderente.slug!,
          twilioRoomName: eventData.twilioRoomName,
          twilioUdaToken: eventData.udaTwilioToken,
          ramal: sip.paNumber,
          callId: call.id,
          paQueueId: userInfo.id,
        });

        // Update Call
        dispatch(
          updateCall({
            ...callDetailsResponse.data,
            finishStatuses: finishStatuses.data,
          }),
        );

        const ramal = voipDialerInstance.getRamal();

        // Update external call
        await updateExternalCall(callDetails.aderenteSlug, currentExternalCall?.externalId!, {
          pk: currentExternalCall?.externalId,
          tipo_ligacao: "atendida",
          ...(ramal ? { ramal_chamada: ramal } : {}),

          numero_externo: currentExternalCall?.numberCalled,
          chamada_interna: call.id,
          ramal_atendente: sip.paNumber,
        });

        dispatch(
          updateTwilioTokens({
            twilioRoomName: eventData.twilioRoomName,
            twilioToken: eventData.paTwilioToken,
          }),
        );
      }),
    );
  };

  // Init call conference
  const initCall = async () => {
    if (callDetails?.callType === "ICOM_VOZ") return initReversaCall();

    let call: Call;
    let markInitCall;
    const updateCallObj: any = {};

    // Load call details / Prevents reload call details in transference scenarios
    if (callDetails && incomingCallId !== callDetails.id) {
      const { data: callData, success: callDetailsSuccess } = await getCallDetails(incomingCallId!);

      if (callDetailsSuccess) {
        dispatch(
          callDataLoaded({
            call: { ...callData /* finishStatuses: finishStatuses.data  */ },
          }),
        );
      }

      call = new Call(callData);
    } else call = callDetails!;

    // Get dialer
    const foundVoipDialer = call.dialerInstances?.find((d) => d.instanceName === "VOIP");
    let voipDialerInstance: DialerInstance;

    // Create dialer binds
    if (foundVoipDialer) voipDialerInstance = foundVoipDialer;
    else voipDialerInstance = await createDialerInstance({ remoteAudioEl: audioElm.current || undefined }, "VOIP", { aderenteSlug: call.aderenteSlug });

    // INIT CARROSSEL
    if (call.pstnNumbers.length) {
      // Register numbers
      for (const pstnNumber of call.pstnNumbers) {
        const { data, success } = await logExternalCall(call.aderenteSlug, {
          tipo_ligacao: "efetuada",
          numero_externo: pstnNumber.requestedNumber,
          chamada_interna: call.id,
          ramal_atendente: sip.paNumber,
        });

        if (success) pstnNumber.externalId = data.id;
        else toast.error(`API: Falha ao pré-registrar chamada externa.`);
      }

      // Configure sip
      const firstNumber = call.pstnNumbers[0];
      firstNumber.active = true;

      // Set numbers
      updateCallObj.pstnNumbers = call.pstnNumbers;
      voipDialerInstance.createCall(firstNumber.requestedNumber!);
    }

    // Load finish statuses
    const finishStatuses = await getStatusFinalizadorByAderente(call.aderenteSlug);

    // Load finishesStatuses
    updateCallObj.finishStatuses = finishStatuses.data;

    // In transference && is destination
    if (call?.transferenceStatus.inTransference) {
      markInitCall = { success: true };

      const externalCalls = await getExternalCalls(call.aderenteSlug, tempData.callId);

      if (call.features.includes("CARROSSEL") && externalCalls?.data?.length) {
        let pstnNumbers = [];
        for (let callIndex in externalCalls.data) {
          const externalCall = externalCalls.data[callIndex];

          if (externalCall.status === null || tempData.currentExternalCallNumber === externalCall.numero_efetivo) {
            const ramal = voipDialerInstance.getRamal();
            const savedNumber = await logExternalCall(call.aderenteSlug, {
              tipo_ligacao: "efetuada",
              numero_externo: externalCall.numero_externo,
              numero_efetivo: externalCall.numero_efetivo,
              chamada_interna: call.id,
              ...(ramal ? { ramal_chamada: ramal } : {}),
              ramal_atendente: sip.paNumber,
              protocolo: externalCall.protocolo,
              status: "nao_realizada",
              inicio_chamada: moment().utcOffset(-3).format("YYYY-MM-DDTHH:mm:ss"),
            });

            if (savedNumber.success)
              // call.numero_externo, false, call.protocolo, savedNumber.data.id, call.numero_externo
              pstnNumbers.push(
                new PSTNCall({
                  requestedNumber: externalCall.numero_externo,
                  numberCalled: externalCall.numero_externo,
                  called: false,
                  externalId: savedNumber.data.id,
                }),
              );
          }
        }

        if (!pstnNumbers.length && tempData.currentExternalCallNumber) {
          const ramal = voipDialerInstance.getRamal();

          const savedNumber = await logExternalCall(call.aderenteSlug, {
            tipo_ligacao: "efetuada",
            numero_externo: tempData.currentExternalCallNumber,
            numero_efetivo: tempData.currentExternalCallNumber,
            ...(ramal ? { ramal_chamada: ramal } : {}),

            chamada_interna: call.id,
            ramal_atendente: sip.paNumber,
            status: "nao_realizada",
            inicio_chamada: moment().utcOffset(-3).format("YYYY-MM-DDTHH:mm:ss"),
          });

          if (savedNumber.success)
            // call.numero_externo, false, call.protocolo, savedNumber.data.id, call.numero_externo
            pstnNumbers.push(
              new PSTNCall({
                requestedNumber: tempData.currentExternalCallNumber,
                numberCalled: tempData.currentExternalCallNumber,
                called: false,
                externalId: savedNumber.data.id,
              }),
            );
        }

        // Save numbers
        updateCallObj.pstnNumbers = pstnNumbers;

        if (updateCallObj.pstnNumbers.length) {
          updateCallObj.pstnNumbers[0].active = true;
          voipDialerInstance.createCall(tempData.currentExternalCallNumber || updateCallObj.pstnNumbers[0].requestedNumber);
        }
      } else if (tempData.currentExternalCallNumber) {
        await voipDialerInstance.createSimpleUser(call.aderenteSlug);

        // Create internal call
        voipDialerInstance.createCall(tempData.currentExternalCallNumber);
      }

      // Update call
      dispatch(
        updateCallObject({
          ...updateCallObj,
        }),
      );

      const endDestTransference = async () => {
        // Mount events
        const acceptEvent: {
          type: string;
          data: { sourcePaQueueId?: string; ramalVoip?: string };
        } = {
          type: "CALL_TRANSFER_REQUEST_ACCEPTED",
          data: {
            sourcePaQueueId: call.transferenceStatus.sourcePaQueueId,
          },
        };

        if (tempData.currentExternalCallNumber) {
          const createClientResult = await voipDialerInstance.createSimpleUser(call.aderenteSlug);

          // Case error
          if (!createClientResult.success && !createClientResult.data.ramal) {
            toast.error("Falha ao atender chamada transferida por falha na alocação de ramal.");
          }

          acceptEvent.data.ramalVoip = createClientResult.data.ramal;
        }

        const udaNotify = {
          type: "NOTIFY_UDA_CALL_TRANSFER_INFO",
          data: {
            callId: call.id,
            udaQueueId,
            sourcePaQueueId: call.transferenceStatus.sourcePaQueueId,
          },
        };

        ws?.sendMessage(acceptEvent);
        ws?.sendMessage(udaNotify);
      };

      // Check if needs pre call
      if (call?.callType === "video" && call.transferenceStatus.isConferenceNeeded)
        setState((s) => ({
          ...s,
          videoCallback: endDestTransference,
        }));
      else {
        endDestTransference();
      }
    } else {
      dispatch(
        updateCallObject({
          ...updateCallObj,
        }),
      );
      markInitCall = await fetchRetry([call?.id, call?.aderenteSlug], registerStartCallConference, 3, 1000);
    }

    // If Call was loaded
    if (!call || !markInitCall.success) {
      alert("Falha ao pegar/iniciar dados da chamada.");
      return endCall(true);
    }

    // Dialer binds
    voipDialerInstance.on("onCallAnswered", async ({ args: { mediaStream, currentCall, ramal } }: { args: { mediaStream: MediaStream; currentCall: DialerPSTN; ramal: string } }) => {
      // Add streams
      Recorder.addMediaStreams([mediaStream]);

      if (currentCallRef.current?.features.includes("CARROSSEL") && currentCallRef.current?.pstnNumbers.length) {
        const foundActivePstn = currentCallRef.current.pstnNumbers.find((n) => n.active);

        let response;

        if (foundActivePstn && !foundActivePstn?.called && foundActivePstn.externalId) {
          response = await updateExternalCall(currentCallRef.current.aderenteSlug, foundActivePstn.externalId, {
            tipo_ligacao: "efetuada",
            status: "atendida",
            ...(ramal ? { ramal_chamada: ramal } : {}),

            inicio_chamada: moment().format("YYYY-MM-DDTHH:mm:ss"),
            numero_efetivo: currentCall.numberCalled,
          });

          // Update call
          foundActivePstn.called = true;
          foundActivePstn.numberCalled = currentCall.numberCalled;

          // Update in redux
          dispatch(updatePstnCall(foundActivePstn));
        } else if (foundActivePstn) {
          response = await logExternalCall(currentCallRef.current.aderenteSlug, {
            tipo_ligacao: "efetuada",
            numero_externo: foundActivePstn?.requestedNumber,
            numero_efetivo: foundActivePstn?.numberCalled,
            chamada_interna: currentCallRef.current.id,
            ramal_atendente: sip.paNumber,
            ...(ramal ? { ramal_chamada: ramal } : {}),
            inicio_chamada: moment().format("YYYY-MM-DDTHH:mm:ss"),
            status: "atendida",
          });
          // teste

          // Update call
          foundActivePstn.called = true;
          foundActivePstn.externalId = response.data.id;

          // Dispatch
          dispatch(addPstnCall(foundActivePstn));
        }
      } else {
        const pstnCallToFinish = currentCallRef.current?.pstnNumbers.find((p) => p.active);

        // Se for uma chamada iniciada por este atendimento só atualiza
        if (pstnCallToFinish?.externalId)
          await updateExternalCall(call.aderenteSlug, pstnCallToFinish.externalId, {
            tipo_ligacao: "efetuada",
            status: "atendida",
          });
        // Se for uma chamada TRANSFERIDA
        else {
          // Register call
          const { success, data, message } = await logExternalCall(call.aderenteSlug, {
            tipo_ligacao: "efetuada",
            numero_externo: currentCall.numberCalled,
            numero_efetivo: currentCall.numberCalled,
            chamada_interna: call.id,
            ramal_atendente: sip.paNumber,
            ramal_chamada: ramal,
            inicio_chamada: moment().utcOffset(-3).format("YYYY-MM-DDTHH:mm:ss"),
            status: "atendida",
          });

          if (!success) return console.error(message);

          // Add IDs
          const pstnCall = new PSTNCall({
            numberCalled: currentCall.numberCalled,
            requestedNumber: currentCall.numberCalled,
            called: true,
          });

          pstnCall.externalId = data.id;
          pstnCall.called = true;
          pstnCall.active = true;

          return dispatch(addPstnCall(pstnCall));
        }
      }
    });

    // On transfer received
    voipDialerInstance.on("onCallReceived", async ({ args: { session } }: { args: { session: Invitation } }) => {
      if (currentCallRef.current?.transferenceStatus.inTransference) {
        session.accept();

        dispatch(
          setTransferStatus({
            inTransference: false,
            isConferenceNeeded: false,
            isTransferenceOrigin: false,
          }),
        );
      }
    });

    voipDialerInstance.on("onCallHangup", async ({ args: { currentCall } }: { args: { currentCall: DialerPSTN } }) => {
      const pstnCallToFinish = currentCallRef.current?.pstnNumbers.find((p) => p.active);

      if (pstnCallToFinish && pstnCallToFinish.externalId) {
        await finishExternalCall(currentCallRef.current!.aderenteSlug, pstnCallToFinish.externalId);
      }

      if (!currentCallRef.current?.features.includes("CARROSSEL") && pstnCallToFinish) {
        pstnCallToFinish.active = false;
        dispatch(updatePstnCall(pstnCallToFinish));
      }
    });

    voipDialerInstance.on("onCallCreated", async (args: { ramal: string; args: { currentCall: DialerPSTN } }) => {
      const {
        ramal,
        args: { currentCall },
      } = args;
      const pstnCallToFinish = currentCallRef.current?.pstnNumbers.find((p) => p.active);

      if (currentCallRef.current?.features.includes("CARROSSEL") && callDetails?.id && pstnCallToFinish?.externalId)
        // Carrosel
        await updateExternalCall(callDetails.aderenteSlug, pstnCallToFinish.externalId, {
          ramal_chamada: args.ramal,
          tipo_ligacao: "efetuada",
        });
      else {
        // Register call
        const { success, data, message } = await logExternalCall(call.aderenteSlug, {
          tipo_ligacao: "efetuada",
          numero_externo: currentCall.numberCalled,
          numero_efetivo: currentCall.numberCalled,
          chamada_interna: call.id,
          ramal_atendente: sip.paNumber,
          ramal_chamada: ramal,
        });

        if (!success) return console.error(message);

        // Add IDs
        const pstnCall = new PSTNCall({
          numberCalled: currentCall.numberCalled,
          requestedNumber: currentCall.numberCalled,
          called: true,
        });

        pstnCall.externalId = data.id;
        pstnCall.called = true;
        pstnCall.active = true;

        return dispatch(addPstnCall(pstnCall));
      }
    });

    // Save dialer instance
    dialerInstances.push(voipDialerInstance);
    dispatch(addDialerInstance(voipDialerInstance));

    // Save on extension
    sendChromeEvent({
      type: "SF/SET_CALL",
      payload: {
        token: accessToken,
        callDetails: call,
      },
    });

    // On WebSocket Message
    // Websocket binds
    wsBinds.push(
      ws.on("UDA_SENT_TEXT_MESSAGE", (data: { text: string }) => {
        let message;

        try {
          message = JSON.parse(data.text);
        } catch (ex) {
          message = data.text;
        }

        if (callDetails) _addMessage(false, callDetails?.uda.usuario.nome, message);
      }),
    );

    wsBinds.push(
      ws.on("PA_TRANSFER_CONFERENCE_END", () => {
        if (!tempData.currentExternalCallNumber)
          // End transference
          dispatch(
            setTransferStatus({
              inTransference: false,
              isConferenceNeeded: false,
              isTransferenceOrigin: false,
            }),
          );
      }),
    );

    // To clear later
    wsBinds.push(
      ws.on("UDA_IS_TYPING", () => {
        clearInterval(udaTypingInterval.current);

        setState((s) => ({ ...s, udaTyping: true }));

        udaTypingInterval.current = window.setTimeout(function () {
          setState((s) => ({ ...s, udaTyping: false }));
        }, 3000);
      }),
    );

    // Event: CALL_TRANSFER_REQUEST_ACCEPTED
    wsBinds.push(
      ws.on("CALL_TRANSFER_REQUEST_ACCEPTED", (event: TransferEvent) => {
        initSourceCallTransferProccess(event);
      }),
    );

    // Event: CALL_TRANSFER_REQUEST_REJECTED
    wsBinds.push(ws.on("CALL_TRANSFER_REQUEST_REJECTED", receiveRejectTransferEvent));

    setState((s) => ({ ...s, loading: false }));
  };

  // End call conference
  const endCall = async (paEnded = false, putCallback?: boolean) => {
    let sendPaEndedCall = paEnded;

    if (callEnding.current) return false;
    callEnding.current = true;

    if (endTransferenceCb.current) {
      await endTransferenceCb.current();
      sendPaEndedCall = false;
    }

    setState((s) => ({ ...s, loading: true }));

    if (putCallback === true) {
      ws?.sendMessage({
        type: "PA_ENDED_CALL",
        data: {
          udaQueueId: udaQueueId,
          shouldCallAgain: true,
          numbersCalled: callDetails?.pstnNumbers.filter((number) => number.called).map((n) => n.requestedNumber),
        },
      });
    }

    if (callDetails) {
      await updateCallConference(callDetails.id, {
        texto_webchat: callDetails.messageListToText(),
      });

      await finishCallConference(callDetails.id, callDetails.aderenteSlug);
    }

    if (sendPaEndedCall && putCallback !== true) {
      ws?.sendMessage({
        type: "PA_ENDED_CALL",
        data: {
          udaQueueId: udaQueueId,
        },
      });
    }

    // Mark feedback
    ws?.sendMessage({
      type: "PA_SET_STATUS_REPORT",
      data: {},
    });

    let dialerPromises: Promise<any> | undefined;
    if (callDetails?.dialerInstances) dialerPromises = Promise.all(callDetails.dialerInstances.map((i) => i.destroy()));

    const result = await getFeedback();
    const callData = await updateCallConference(callDetails!.id, result);

    setState((s) => ({ ...s, waitingFeedback: false }));

    if (dialerPromises) await dialerPromises;

    if (!callData.success)
      Swal.fire({
        title: "ERRO NO ENVIO DAS OBSERVAÇÕES",
        text: "Por favor peça ajuda ao suporte técnico.",
        icon: "error",

        showCloseButton: false,
        showCancelButton: false,
        showConfirmButton: false,

        timer: 5000,
      });

    dispatch(callEnded());
  };

  // Add Text Message core
  const _addMessage = async (self: boolean = false, name: string, text: string) => {
    // Send message
    if (self)
      ws?.sendMessage({
        type: "PA_SENT_TEXT_MESSAGE",
        data: {
          udaQueueId: udaQueueId,
          text: JSON.stringify(text),
        },
      });

    const message = {
      id: Math.random(),
      name: name,
      created_at: new Date(),
      text,
      self,
    };
    // Dispatch
    dispatch(addMessage(message));

    // Save history
    await updateCallConference(callDetails!.id, {
      texto_webchat: callDetails!.messageListToText(message),
    });
  };

  // Init source transfer event
  const initSourceCallTransferProccess = async (event: any) => {
    // TODO: PAUSE TAX TIMER
    const currentCall = currentCallRef.current;

    endTransferenceCb.current = async () => {
      // DIALER
      let voipDialerInstance = currentCall?.dialerInstances?.find((d) => d.instanceName === "VOIP");

      if (voipDialerInstance?.getStatus() === DialerStatus.ON_CALL && event.ramalVoip) {
        // await new Promise((resolve) => setTimeout(resolve, 2000));

        voipDialerInstance.transferCall(event.ramalVoip);
      }
      // TODO: MARK CALLS NON ATTENDED
      if (currentCall?.pstnNumbers.length) {
        const nonCalledNumber = currentCall.pstnNumbers.filter((pstn) => !pstn.called);

        for (let number of nonCalledNumber) {
          await updateExternalCall(currentCall.aderenteSlug, number.externalId!, {
            tipo_ligacao: "efetuada",
            status: "transferida",
          });
        }
      }

      if (currentCall?.transferenceStatus.inTransference && transferPaSelectedRef.current) {
        const wsEvent = {
          type: "PA_TRANSFER_CONFERENCE_END",
          data: {
            destinationPaQueueId: transferPaSelectedRef.current,
          },
        };
        ws?.sendMessage(wsEvent);
      }

      await endCall(false);

      endTransferenceCb.current = null;
    };

    if (currentCall?.transferenceStatus.inTransference && !currentCall?.transferenceStatus.isConferenceNeeded) {
      endTransferenceCb.current(); // && hasPaConference
    }
  };

  // Receive transfer event
  const receiveRejectTransferEvent = async () => {
    await Swal.fire({
      title: "TRANSFERENCIA DE CHAMADA RECUSADA!",
      text: "O atendendente para quem você tentou transferir a ligação rejeitou, a chamada será restaurada.",
      icon: "error",
      timer: 5000,
      showConfirmButton: false,
      showCancelButton: false,
    });

    // TODO: UPDATE VIDEO CONTROLS
    //  if (self.call.currentCall_Info().callType() === "video") {
    //    self.call.enableVideo();
    //    self.call.enableAudio();
    //    self.dialer.unmuteMic();
    //  }

    // Update call trasnference status
    dispatch(
      setTransferStatus({
        inTransference: false,
        isConferenceNeeded: false,
        isTransferenceOrigin: false,
      }),
    );
  };

  // Update status_finalizador
  const setFinishStatus = async (finishStatus: string) => {
    if (!callDetails) return false;

    const result = await updateCallConference(callDetails.id, {
      status_finalizador: finishStatus,
    });

    if (result.success) {
      return dispatch(
        updateCallObject({
          finishStatus: finishStatus,
        }),
      );
    }

    return true;
  };

  // Call feedbackscreen
  const getFeedback = (): Promise<{
    observacao: string;
    status_finalizador: string;
  }> =>
    new Promise((resolve) => {
      setState((s) => ({ ...s, waitingFeedback: true }));
      resolveRef.current = resolve; // setTimeout(() => resolve(false), maxTimeout);
    });

  // Protect route
  useEffect(() => {
    async function demountCall() {
      if (!incomingCallId) {
        if (callDetails) await endCall();

        // Update app status for IDLE
        dispatch(setIdle());

        // Set available
        ws?.sendMessage({
          type: "PA_SET_STATUS_AVAILABLE",
          data: {
            isWaitingTransfer: false,
          },
        });

        return navigate("/");
      }
    }

    demountCall();
  }, [incomingCallId]);

  // Reference updater
  useEffect(() => {
    currentCallRef.current = callDetails;
    transferPaSelectedRef.current = transferPaSelected;
  }, [callDetails, transferPaSelected]);

  // Start call conference & Hooks
  useEffect(() => {
    // Save Dialer Output
    audioElm.current = document.getElementById("remote-dialer-audio") as HTMLAudioElement;

    if (incomingCallId) initCall();

    async function onBeforeUnload(e: BeforeUnloadEvent) {
      sendChromeEvent({
        type: "SF/FINISH_CALL",
      });

      return delete e["returnValue"];
    }

    // Before unload event
    window.addEventListener("beforeunload", onBeforeUnload);

    // Block Refresh Keys
    document.onkeydown = function (e) {
      if (e.key === "F5") {
        return false;
      }
    };

    return () => {
      // Clear binds
      document.onkeydown = null;
      window.removeEventListener("beforeunload", onBeforeUnload);

      // Remove old binds
      ws?.clearBinds(wsBinds);
    };
  }, []);

  return (
    <div className={`screen flex call grow`}>
      {state.loading && !state.waitingFeedback && <ScreenLoader />}
      {!state.loading && callDetails && callDetails.callType === "video" && (
        <VideoChat
          startCallBack={state.videoCallback}
          twilioRoomName={twilioRoomName!}
          twilioToken={twilioToken!}
          innerRef={twilioRef}
          endCall={endCall}
          addMessage={_addMessage}
          udaIsTyping={state.udaTyping}
          setFinishStatus={setFinishStatus}
          callEnding={callEnding.current}
        />
      )}
      {!state.loading && callDetails && callDetails.callType === "texto" && (
        <TextChat addMessage={_addMessage} setFinishStatus={setFinishStatus} udaIsTyping={state.udaTyping} endCall={endCall} />
      )}

      {/* ICOM_VOZ */}
      {!state.loading && callDetails && ["ICOM_VOZ", "reversa"].includes(callDetails.callType) && (
        <ReversaCall
          innerRef={twilioRef}
          twilioRoomName={twilioRoomName!}
          twilioToken={twilioToken!}
          callEnding={callEnding.current}
          endCall={endCall}
          startCallBack={state.videoCallback}
          setFinishStatus={setFinishStatus}
        />
      )}

      {!state.loading && callDetails && callDetails.callType === "extension" && <ExtensionChat twilioRoomName={twilioRoomName!} twilioToken={twilioToken!} endCall={endCall} />}

      <Feedback
        finishStatuses={[...(callDetails?.finishStatuses || [])]}
        preSelectedStatus={callDetails?.finishStatus}
        onSubmit={(response: { status_finalizador: string; observation: string }) => resolveRef.current && resolveRef.current(response)}
        show={state.waitingFeedback}
      />
    </div>
  );
}
